import { InjectionToken } from '@angular/core';

/**
 * Registration of a slice that opts into generic IndexedDB persistence. `persistSelector`
 * projects the slice to its serializable, store-safe shape (the exact value written on
 * `persistSlice`). The concurrency-store entry key is derived from `featureKey`, see
 * {@link persistedSliceStorageKey}.
 */
export interface PersistedSlice {
    featureKey: string;
    persistSelector: (state: object) => unknown;
    /**
     * Keep the persisted copy on this device only. By default a persisted slice is also part of
     * the device handover: `ConcurrentUsageService.uploadAll` uploads it on check-out and
     * `downloadAll` restores it on check-in. A slice whose data already lives in Azure by other
     * means sets this, so its local cache is never uploaded as a second copy — the user-store
     * slice does, since it mirrors the user store itself.
     */
    localOnly?: boolean;
}

/**
 * The concurrency-store entry key a persisted slice is written under.
 *
 * @param featureKey the slice's feature key
 * @returns the storage entry key
 */
export function persistedSliceStorageKey(featureKey: string): string {
    return `${featureKey}Slice`;
}

/**
 * Multi-provider token: each persisted slice's store module contributes one {@link
 * PersistedSlice}. `PersistenceEffects` collects them into a registry so a slice can be
 * persisted/rehydrated purely by feature key, without persistence depending on the slices.
 */
export const PERSISTED_SLICES = new InjectionToken<PersistedSlice[]>('PERSISTED_SLICES');
